<template>
  <div class="section-complementaries h-100">
    <div class="h-100 d-flex flex-column">
      <div class="d-flex flex-column flex-grow-1 section-complementaries-layout-height">
        <b-row class="d-flex align-items-center mb-2 mt-1">
          <b-col class="d-flex align-items-center">
            <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.complementaryInformation') }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <div class="d-flex align-items-center">
              <vt-loading-button
                v-if="!complementary.validationDate && $_canDeleteComplementary()"
                qa="section-complementaries-delete-button"
                class="btn__icon btn__icon--primary px-2"
                :params="{ promise: deleteComplementary }"
              >
                <i
                  v-tooltip="$t('diagnosis.btns.deleteComp')"
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </vt-loading-button>
              <b-button
                v-if="$_canAttachDocumentToComplementary()"
                id="comp-more"
                :disabled="disabledAttachButton(complementary)"
                class="btn__icon btn__icon--primary pl-2 pr-1"
              >
                <i
                  class="fas fa-ellipsis-v"
                  aria-hidden="true"
                />
              </b-button>
              <b-popover
                v-if="$_canAttachDocumentToComplementary()"
                target="comp-more"
                placement="bottom"
                triggers="click blur"
              >
                <div class="ap-popover">
                  <ul>
                    <li
                      v-if="$_canAttachDocumentToComplementary()"
                    >
                      <b-button
                        :disabled="disabledAttachPopoverButton(complementary)"
                        @click="showAttachmentModal"
                      >
                        {{ $t('diagnosis.btns.attach') }}
                      </b-button>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col
            xl="5"
            lg="8"
          >
            <div class="form-label">
              <label>{{ $t('diagnosis.labels.pathologist') }}</label>
            </div>
            <vt-combo-select
              v-model="complementary.responsible"
              :disabled="!!complementary.validationDate || !$_canEditComplementary()"
              track-by="id"
              label="fullName"
              :allow-empty="false"
              :option-loader="getPersonnelPathologist"
              qa="section-complementaries-pathologist-select"
            />
          </b-col>
          <b-col
            xl="2"
            lg="4"
          >
            <div class="form-label">
              <label>{{ $tc('global.sample', 2) }}</label>
            </div>
            <vt-combo-select
              v-model="complementary.sample"
              :disabled="!!complementary.validationDate || !$_canEditComplementary()"
              track-by="samId"
              label="samSamplenumber"
              :allow-empty="false"
              :option-loader="getSamples"
              qa="section-complementaries-samples-select"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <hr>
          </b-col>
        </b-row>
        <description-editor
          v-if="attachmentTypeIdForComplementaryAudio"
          ref="complementaryInformation"
          :disabled="!!complementary.validationDate"
          :description-title="$t('global.description.modal.description')"
          :description-service="complementaryInformationService"
          :parent-entity-ids-array="[labId, casId, complementaryId]"
          :laboratory-id="labId"
          :case-id="casId"
          :attachment-type-id-for-description-audio="attachmentTypeIdForComplementaryAudio"
          :attachment-service-for-audio-attachments="complementaryAttachmentService"
          :attachment-service="attachmentService"
          :root-filename-for-audio-description="`${complementaryId}-ComplementaryAudio-`"
          :hide-edit-operations="!$_canEditComplementary()"
          :hide-attach-operations="!$_canAttachDocumentToComplementary()"
          :disable-attach-operations="!disableAttachOperations"
          :complementary-id="complementaryId"
          :diagnosis-section="DIAGNOSIS_COMPLEMENTARY_SECTION"
          qa="section-complementaries-description-editor"
          @attachments-updated="onAttachmentsAdded"
          @descriptionLoaded="getDescriptionLoaded($event)"
        />
      </div>
      <div class="d-flex justify-content-end">
        <vt-loading-button
          v-if="$_canEditComplementary()"
          :disabled="!!complementary.validationDate"
          class="btn__outline btn-sm mt-2"
          qa="section-complementaries-save-button"
          :params="{ promise: save, shortkeys: ['alt', 's'] }"
        >
          <i
            class="fal fa-save mr-2"
            aria-hidden="true"
          />
          {{ $tc('global.save') }}
        </vt-loading-button>
        <vt-loading-button
          v-if="complementary.canBeValidated"
          qa="section-complementaries-validate-button"
          class="btn--primary ml-2 btn-sm mt-2"
          :params="{ promise: checkResponsibleAndValidateComplementary }"
        >
          <i
            class="fal fa-check mr-2"
            aria-hidden="true"
          />
          {{ $tc('diagnosis.btns.validateComp') }}
        </vt-loading-button>
        <vt-loading-button
          v-if="complementary.canBeInvalidated"
          qa="section-complementaries-remove-validation-button"
          class="btn--primary ml-2 btn-sm mt-2"
          :params="{ promise: removeValidation }"
        >
          <i
            class="fal fa-undo mr-2"
            aria-hidden="true"
          />
          {{ $tc('diagnosis.btns.removeValidate') }}
        </vt-loading-button>
      </div>
    </div>
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import attachmentTypeService, { ATTACHMENT_TYPES } from '@/api/resources/attachment/attachmentTypeService'
import { personnelService } from '@/api/resources/personnelService'
import commonSampleService from '@/api/resources/common/commonSampleService'
import complementaryServices from '@/modules/diagnosisModule/services/complementaryServices'
import DescriptionEditor from '@/components/description/DescriptionEditor'
import complementaryInformationService from '@/api/resources/description/ComplementaryInformationService'
import complementaryAttachmentService from '@/api/resources/attachment/complementaryAttachmentService'
import { cloneDeep, isEqual } from 'lodash'
import AddFileModal from '@/components/AddFileModal'
import apiExceptionType from '@/utils/apiExceptionType'
import complementaryInformationAttachmentService from '@/api/resources/description/attachment/complementaryInformationAttachmentService'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { ComplementaryOperationGuardMixin } from '@/api/operations/mixins/guards/complementary-operation-guard.mixin'
import { CaseOperation } from '@/api/operations/models/case-operation'
import { SECTION_COMPLEMENTARIES } from '@/modules/diagnosisModule/constants/diagnosis-sections'
import { CommonResponsibilityCodeEnum } from '@/api/services/common/responsibility/common-responsibility.dto'

export default {
    name: 'SectionComplementaries',
    components: {
        DescriptionEditor,
        AddFileModal
    },
    mixins: [
        ComplementaryOperationGuardMixin
    ],
    props: {
        title: [String, Number],
        labId: { type: Number, required: true },
        casId: { type: Number, required: true },
        complementaryId: { type: Number, required: true },
        isCaseInAmendment: { type: Boolean, default: false }
    },
    data() {
        return {
            attachmentTypeIdForComplementaryAudio: null,
            originalComplementary: null,
            responsible: null,
            showAudio: false,
            showCompCol: true,
            selectedCardId: null,
            complementaryInformationText: '',
            refreshEditor: 0,
            complementary: {},
            showCod: true,
            selected: null,
            complementaryDescription: null
        }
    },
    computed: {
        DIAGNOSIS_COMPLEMENTARY_SECTION() {
            return SECTION_COMPLEMENTARIES
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.COMPLEMENTARY
        },
        entitiesIdArray() {
            if (this.complementaryId) {
                return [this.labId, this.casId, this.complementaryId]
            }
            return [this.labId, this.casId, null]
        },
        complementaryInformationService() {
            return complementaryInformationService
        },
        complementaryAttachmentService() {
            return complementaryAttachmentService
        },
        attachmentService() {
            return complementaryInformationAttachmentService
        },
        sectionHasData() {
            return !!(this.complementary && (this.complementary.responsible || this.complementary.sample || !!(this.complementaryDescription)))
        }
    },
    watch: {
        selectedCardId() {
            if (this.complementary.complementaryInformation) {
                this.complementaryInformationText = this.complementary.complementaryInformation.text
            } else {
                this.complementaryInformationText = ''
            }
            this.refreshEditor++
        }
    },
    created() {
        this.loadComplementary()
        this.loadAttachmentTypeIdForComplementaryInformationAudio()
    },
    methods: {
        async loadDescriptionAudioAttachments() {
            await this.$refs.complementaryInformation.loadDescriptionAudioAttachments()
        },
        disableAttachOperations() {
            const complementaryMetadata = {
                metadata: {
                    operationGuards: {
                        canBeAttached: this.complementary.canBeAttached
                    }
                }
            }
            return this.$_canBeDocumentAttachedToComplementary(complementaryMetadata)
        },
        disabledAttachButton(complementary) {
            return !this.canBeDocumentAttachedToComplementary(complementary)
        },
        disabledAttachPopoverButton(complementary) {
            return !this.canBeDocumentAttachedToComplementary(complementary)
        },
        canBeDocumentAttachedToComplementary(complementary) {
            // TODO: Endpoint complementary DTO pending changes to include metadata
            const complementaryMetadata = {
                metadata: {
                    operationGuards: {
                        canBeAttached: complementary.canBeAttached
                    }
                }
            }
            return this.$_canBeDocumentAttachedToComplementary(complementaryMetadata)
        },
        getDescriptionLoaded(data) {
            this.complementaryDescription = data
            this.$emit('reloadSummary', this.sectionHasData)
        },
        showAttachmentModal() {
            this.$refs.addFileModalWrapper.show()
        },
        onAttachmentsAdded() {
            this.$emit('loadComplementaries', false)
        },
        async saveDescriptionAudio(file) {
            const [err] = await this.toCall(this.attachmentServiceForAudioAttachments.addAttachment(...this.parentEntityIdsArray, this.attachmentTypeIdForDescriptionAudio, file))
            if (err) {
                if (err.response.data.code === apiExceptionType.MAX_UPLOAD_SIZE_EXCEEDED || err.response.status === apiExceptionType.ENTITY_TOO_LARGE) {
                    this.showErrorModalGlobal(this.$t('component.attachModal.error.maxFileSizeExceeded'))
                } else {
                    this.showErrorModalGlobal(this.$t('global.description.error.uploadingAudio'))
                }
            } else {
                await this.loadDescriptionAudioAttachments()
                this.showToastSuccessGlobal()
            }
        },
        hasBeenChanged() {
            return this.$refs.complementaryInformation.hasBeenChanged() || !isEqual(this.originalComplementary, this.complementary)
        },
        async save(showToaster = true) {
            let success = await this.$refs.complementaryInformation.save(false)
            success &= await this.saveComplementaryData(showToaster)
            return success
        },
        async removeValidation() {
            const [err] = await this.toCall(complementaryServices.removeValidationComplementary(this.labId, this.casId, this.complementaryId))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.complementaries.removeValidationSuccess'))
            this.$emit('loadComplementaries', false)
            await this.loadComplementary()
        },
        async checkResponsibleAndValidateComplementary() {
            let responsibleValid = true
            const isLoggedInPersonnelComplementaryPathologist = this.complementary.responsible?.id === this.$store.getters.userInformation?.personnel?.id
            if (!isLoggedInPersonnelComplementaryPathologist) {
                responsibleValid = this.checkCorrectPathologist(CaseOperation.GLOBAL_VALIDATE)
            } else {
                responsibleValid = true
            }
            let loading = false
            if (!responsibleValid) {
                const shouldAssignMyselfAsResponsible = await this.showConfirmationModalGlobal(this.$t('diagnosis.complementaries.wrongResponsibleConfirmation'))
                loading = true
                if (shouldAssignMyselfAsResponsible) {
                    responsibleValid = await this.assignLoggedUserAsResponsible()
                    await this.loadComplementary()
                }
            }
            if (responsibleValid) {
                let successfulSave = true
                if (this.hasBeenChanged()) {
                    successfulSave = await this.save(false)
                }
                if (successfulSave) {
                    await this.validateComplementary()
                }
            }
        },
        async assignLoggedUserAsResponsible() {
            this.complementary.responsible = cloneDeep(this.$store.getters.userInformation?.personnel)
            return this.internalSaveComplementaryData()
        },
        async validateComplementary() {
            const [err] = await this.toCall(complementaryServices.validateComplementary(this.labId, this.casId, this.complementaryId))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.complementaries.complementaryValidated'))
            await this.loadComplementary()
        },
        async saveComplementaryData(showToaster = true) {
            const success = await this.internalSaveComplementaryData()
            if (success) {
                if (showToaster) {
                    this.showToastSuccessGlobal()
                }
                await this.loadComplementary()
            }
            return success
        },
        async internalSaveComplementaryData() {
            const complementaryData = {
                samId: this.complementary.sample && this.complementary.sample.samId,
                responsibleId: this.complementary.responsible ? this.complementary.responsible.id : null,
                observations: this.complementary.observations,
                originalConcurrencyUpdatedAt: this.complementary.originalConcurrencyUpdatedAt
            }
            this.updateLoading(true)
            const [err] = await this.toCall(complementaryServices.updateComplementary(this.labId, this.casId, this.complementaryId, complementaryData))
            this.updateLoading(false)
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$tc('diagnosis.btns.complementary', 1), err, () => {
                        this.complementary.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.internalSaveComplementaryData()
                    })
                } else {
                    this.showErrorModalGlobal()
                    return false
                }
            }
            return true
        },
        async loadAttachmentTypeIdForComplementaryInformationAudio() {
            this.updateLoading(true)
            const [error, response] = await this.toCall(attachmentTypeService.getAttachmentType('COMPLEMENTARY', ATTACHMENT_TYPES.COMPLEMENTARY_INFORMATION_AUDIO))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobal()
                return
            }
            this.attachmentTypeIdForComplementaryAudio = response.atyId
        },
        async loadComplementary() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(complementaryServices.getComplementaryById(this.labId, this.casId, this.complementaryId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.complementary = response
            this.originalComplementary = cloneDeep(this.complementary)
            this.$emit('reloadSummary', this.sectionHasData)
            this.$emit('updateCase')
            this.$emit('loadComplementaries', false)
        },
        async deleteComplementary() {
            const confirmationResponse = await this.showConfirmationModalGlobal(this.$t('diagnosis.complementaries.deleteComplementaryConfirmation'))
            if (confirmationResponse) {
                const [err] = await this.toCall(complementaryServices.deleteComplementary(this.labId, this.casId, this.complementaryId))
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                this.$emit('loadComplementaries', true)
                this.$emit('updateCase')
            }
        },
        selectCard(cardId) {
            this.selectedCardId = cardId
        },
        showSearchCodeModal() {
            this.$refs['searchcodeModal'].show()
        },
        getPersonnelPathologist() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.labId, CommonResponsibilityCodeEnum.PATHOLOGIST)
        },
        getSamples() {
            return commonSampleService.getSamplesOfCaseAsCards(this.labId, this.casId)
        },
        checkCorrectPathologist(operationCode = null) {
            return operationCode &&
                this.complementary.responsible?.allowsCaseValidationByOtherUsers &&
                this.$_hasLabOperation(this.labId, operationCode)
        },
        isResponsibleValid() {
            return this.complementary.responsible &&
                this.$store.getters.userInformation?.personnel?.id === this.complementary.responsible.id
        }
    }
}
</script>

<style lang="scss">
.section-complementaries .ql-toolbar.ql-snow {
  padding: 2px;
}

.section-complementaries .form-label {
  font-size: font-size(small);
}

.section-complementaries .ql-editor {
  transition: 0.35s;
  overflow-y: auto;
}

.section-complementaries .ag-root-wrapper-body.ag-layout-normal {
  padding: 0 !important;
}

.section-complementaries .audio-scroll {
  max-height: calc(20vh - 10px) !important;
  overflow-y: auto !important;
  margin-right: 0 !important;
}

.section-complementaries .section-complementaries-layout-height {
  min-height: 0;
}

/*--Styles for large screen--*/
@media (max-width: 1300px) {
  .section-complementaries .static-audio-col .ar-icon {
    width: 25px;
    height: 25px;
  }

  .section-complementaries .audio-box .btn {
    font-size: font-size(normal);
  }
}

/*--Sizes for laptop screen--*/
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
  .section-complementaries .ql-editor {
    height: calc(100vh - 500px);
    transition: 0.35s;
    overflow-y: auto;
  }
}
</style>

<style lang="scss" scoped>
.validated {
  background: vt-color(green-radiant);
  color: #FFF !important;
  font-weight: bold;
}

.pending {
  background: vt-color(orange-medium);
  color: #FFF !important;
  font-weight: bold;
}

.codifications-grid {
  height: calc(25vh - 40px) !important;
}

.cod-block {
  cursor: pointer;
  transition: 0.2s;
  color: vt-color(primary);
}

.cod-block:hover {
  color: vt-color(dark-primary);
}

.pathologist {
  font-size: font-size(small);
}

.truncated {
  width: 97%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line {
  padding: 0 10px;
}

.line div {
  width: 1px;
  height: 20px;
  background: rgba(0, 0, 0, 0.1);
}

.comp-btn {
  width: 100%;
  font-size: font-size(small) !important;
}

.comp-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: vt-color(dark-text);
  border-radius: 2px;
  transition: 0.2s;
  cursor: pointer;
}

.active {
  background: vt-color(primary) !important;
  color: vt-color(white-light);
}

.comp-card:hover {
  background: vt-color(light-primary);
}

.static-audio-col {
  flex: 0 0 350px !important;
}

.audio-scroll {
  min-height: calc(100vh - 930px);
  max-height: calc(100vh - 460px);
  overflow-y: auto;
  transition: 0.35s;
}

@media (max-width: 1370px) {
  .audio-scroll {
    min-height: calc(100vh - 915px);
    max-height: calc(100vh - 450px);
    overflow-y: auto;
    transition: 0.35s;
  }

  .static-audio-col {
    flex: 0 0 250px !important;
  }

  .audio-click span {
    width: 90px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: font-size(small);
  }
}

/*--Sizes for laptop screen--*/
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
  .audio-scroll {
    min-height: calc(100vh - 620px);
    max-height: calc(100vh - 470px);
    overflow-y: auto;
    transition: 0.35s;
  }

  .static-audio-col {
    flex: 0 0 250px !important;
  }
}
</style>
